import type { TraceClaim, TraceFinding, TraceStatus } from "../api/types";
import { isTraceBlocking } from "../api/types";

/**
 * Traceability verdict for one quote, with its acknowledgement control.
 *
 * Shared by the quote comparison and the recommendation card so a buyer reads
 * the same words in both places — the two screens sit either side of the same
 * decision, and wording that drifted between them would be read as two
 * different findings.
 *
 * Rendered above the price figures for the same reason the review flag is: a
 * quote with missing paperwork is very often the cheapest one on the page, and
 * the caveat has to be read before the number, not after it.
 */

type Tone = {
  bg: string;
  border: string;
  fg: string;
  label: string;
};

const TONES: Record<TraceStatus, Tone> = {
  // Blocking — red, matching the weight of the decision rather than the amber
  // used for the review flag, which a buyer can clear themselves.
  missing: {
    bg: "#fff5f5",
    border: "#ffc9c9",
    fg: "#a61e1e",
    label: "⚠ No traceability offered",
  },
  incomplete: {
    bg: "#fff5f5",
    border: "#ffc9c9",
    fg: "#a61e1e",
    label: "⚠ Traceability incomplete",
  },
  conflicting: {
    bg: "#fff5f5",
    border: "#ffc9c9",
    fg: "#a61e1e",
    label: "⚠ Wrong certificate for this condition",
  },
  // Non-blocking.
  asserted_unverified: {
    bg: "#f4f8ff",
    border: "#c9dcff",
    fg: "#1c4b8a",
    label: "Trace committed — verify at goods-in",
  },
  satisfied: {
    bg: "#f4fbf6",
    border: "#c3e6cd",
    fg: "#1f7a3d",
    label: "✓ Traceability verified",
  },
  unassessed: {
    bg: "#f6f6f6",
    border: "#e0e0e0",
    fg: "#666",
    label: "Traceability not assessed",
  },
};

// Canonical tokens from services/api-core/app/services/trace_requirement.py.
const CERT_LABELS: Record<string, string> = {
  "8130-3": "FAA 8130-3",
  easa_form_1: "EASA Form 1",
  coc: "CofC",
  ata_106: "ATA 106",
  oem_trace: "OEM trace",
  airline_trace: "Airline trace",
  teardown_report: "Teardown report",
  removal_record: "Removal record",
  btb: "Back-to-birth",
};

/**
 * Human name for a canonical cert token, falling back to the token itself.
 *
 * Exported so the order screens name the same certificate the same way the
 * quote screen does — a buyer who accepted "FAA 8130-3" on a quote should not
 * read "8130-3" on the resulting order and wonder whether it is the same thing.
 */
export function certLabel(token: string): string {
  return CERT_LABELS[token] ?? token;
}

/**
 * Compact one-line summary for the figures grid.
 *
 * Names what the supplier actually committed to when we know it. Falls back to
 * the old yes/no only for quotes that predate the assessment — for those the
 * boolean is genuinely all there is.
 */
export function traceDocsLabel(
  claim: TraceClaim | null | undefined,
  hasTrace: boolean,
): string {
  const types = claim?.asserted_cert_types ?? [];
  if (types.length > 0) {
    return types.map(certLabel).join(", ");
  }
  return hasTrace ? "✓ Unspecified" : "✗ None";
}

export interface TraceStatusPanelProps {
  status?: TraceStatus | null;
  findings?: TraceFinding[] | null;
  claim?: TraceClaim | null;
  /**
   * Flat blocking text, used where the full findings are not available. A
   * scored quote carries this instead of `findings`, because the agents are
   * handed a summary rather than the structured verdict.
   */
  summary?: string;
  /** True when the signed-in user holds manager or admin. */
  canOverride: boolean;
  /** False when the quote cannot be actioned at all (already awarded, etc.). */
  canAct: boolean;
  acknowledged: boolean;
  onAcknowledge: (value: boolean) => void;
}

export function TraceStatusPanel({
  status,
  findings,
  claim,
  summary,
  canOverride,
  canAct,
  acknowledged,
  onAcknowledge,
}: TraceStatusPanelProps) {
  // Nothing to say about a quote that predates the assessment — an empty grey
  // box on every historical quote would be noise, not information.
  if (!status || status === "unassessed") return null;

  const tone = TONES[status] ?? TONES.unassessed;
  const blocking = isTraceBlocking(status);
  const blockingFindings = (findings ?? []).filter(
    (f) => f.severity === "blocking",
  );
  const advisoryFindings = (findings ?? []).filter(
    (f) => f.severity === "advisory",
  );

  return (
    <div
      role={blocking ? "alert" : undefined}
      style={{
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        borderRadius: 4,
        padding: "6px 8px",
        margin: "0 0 8px",
        fontSize: 12,
        color: tone.fg,
      }}
    >
      <strong>{tone.label}</strong>

      {blockingFindings.length > 0
        ? blockingFindings.map((f) => (
            <div key={f.code} style={{ marginTop: 4 }}>
              {f.message}
            </div>
          ))
        : summary && <div style={{ marginTop: 4 }}>{summary}</div>}

      {claim?.raw_text && (
        <div style={{ marginTop: 4, opacity: 0.85 }}>
          Supplier stated: “{claim.raw_text}”
          {claim.source_channel ? ` (via ${claim.source_channel})` : null}
        </div>
      )}

      {advisoryFindings.map((f) => (
        <div key={f.code} style={{ marginTop: 4, opacity: 0.75 }}>
          {f.message}
        </div>
      ))}

      {blocking &&
        canAct &&
        (canOverride ? (
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginTop: 6,
              fontWeight: 600,
            }}
          >
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => onAcknowledge(e.target.checked)}
            />
            I accept this traceability shortfall and authorise the order
          </label>
        ) : (
          // A buyer sees exactly why the order is blocked but cannot clear it:
          // accepting a part whose paperwork will not pass goods-in is an
          // airworthiness call, and the server returns 403 if they try.
          <div style={{ marginTop: 6, fontWeight: 600 }}>
            A manager must review this before the order can be placed.
          </div>
        ))}
    </div>
  );
}
